import api from "../axiosConfig";

export const PROFILE_SETTINGS_CACHE_UPDATED_EVENT =
  "loadevo:profile-settings-cache-updated";

const PROFILE_SETTINGS_CACHE_VERSION =
  "v2";

const PROFILE_SETTINGS_CACHE_MAX_AGE =
  180 *
  24 *
  60 *
  60 *
  1000;

const PROFILE_SETTINGS_FRESH_FOR =
  60 *
  1000;

const LEGACY_PROFILE_CACHE_KEY =
  "profile-settings.v1";

const LEGACY_ACCOUNT_CACHE_KEY =
  "account-access.v1";

const EMPTY_PROFILE_SETTINGS_DATA = {
  profile: null,
  accountEmail: "",
  fetchedAt: 0,
};

let hotCacheKey =
  null;

let hotCacheEnvelope =
  null;

let inFlightPromise =
  null;

let requestSequence =
  0;

let latestRequestId =
  0;

let localPatchRevision =
  0;

let warmedAvatarUrl =
  null;

let warmedAvatarImage =
  null;

let warmedAvatarPromise =
  null;

const safeParse =
  (
    value
  ) => {
    try {
      return JSON.parse(
        value
      );
    } catch {
      return null;
    }
  };

const normalizeEmail =
  (
    value
  ) =>
    String(
      value ||
        ""
    )
      .trim()
      .toLowerCase();

const decodeJwtPayload =
  (
    token
  ) => {
    if (!token) {
      return null;
    }

    try {
      const part =
        token
          .split(".")[1]
          ?.replace(
            /-/g,
            "+"
          )
          .replace(
            /_/g,
            "/"
          );

      if (!part) {
        return null;
      }

      const padded =
        part +
        "=".repeat(
          (
            4 -
            (
              part.length %
              4
            )
          ) %
            4
        );

      return JSON.parse(
        window.atob(
          padded
        )
      );
    } catch {
      return null;
    }
  };

const hashString =
  (
    value
  ) => {
    let hash =
      2166136261;

    const source =
      String(
        value ||
          ""
      );

    for (
      let index = 0;
      index <
      source.length;
      index += 1
    ) {
      hash ^=
        source.charCodeAt(
          index
        );

      hash =
        Math.imul(
          hash,
          16777619
        );
    }

    return (
      hash >>>
      0
    ).toString(
      36
    );
  };

const getStoredToken =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "";
    }

    return (
      window.localStorage.getItem(
        "authToken"
      ) ||
      window.sessionStorage.getItem(
        "authToken"
      ) ||
      window.localStorage.getItem(
        "token"
      ) ||
      window.localStorage.getItem(
        "accessToken"
      ) ||
      ""
    );
  };

export const resolveCurrentUserId =
  () => {
    const payload =
      decodeJwtPayload(
        getStoredToken()
      );

    const raw =
      payload?.id ??
      payload?.user_id ??
      payload?.userId ??
      payload?.sub ??
      null;

    if (
      raw === null ||
      raw === undefined
    ) {
      return null;
    }

    return String(
      raw
    );
  };

const resolveUserScope =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "server";
    }

    const token =
      getStoredToken();

    const userId =
      resolveCurrentUserId();

    if (userId) {
      return `user-${userId}`;
    }

    if (token) {
      return `token-${hashString(
        token
      )}`;
    }

    return "anonymous";
  };

const buildCacheKey =
  () =>
    `loadevo.profile-settings.${resolveUserScope()}.${PROFILE_SETTINGS_CACHE_VERSION}`;

const absoluteFromApiBase =
  (
    path
  ) => {
    if (!path) {
      return null;
    }

    if (
      /^https?:\/\//i.test(
        path
      )
    ) {
      return path;
    }

    const base = (
      api.defaults?.baseURL ||
      ""
    ).replace(
      /\/$/,
      ""
    );

    return base
      ? `${base}${
          path.startsWith("/")
            ? ""
            : "/"
        }${path}`
      : path;
  };

const normalizeProfile =
  (
    data
  ) => ({
    name:
      data?.name ||
      "",

    address:
      data?.address ||
      "",

    city:
      data?.city ||
      "",

    zipCode:
      data?.postalCode ||
      "",

    country:
      data?.country ||
      "",

    idNumber:
      data?.idNumber ||
      "",

    idExpiryDate:
      (
        data?.idExpiryDate ||
        ""
      ).slice(
        0,
        10
      ),

    dob:
      (
        data?.dateOfBirth ||
        ""
      ).slice(
        0,
        10
      ),

    phone:
      data?.phoneNumber ||
      "",

    personalEmail:
      data?.email ||
      "",

    role:
      (
        data?.role ||
        ""
      ).toLowerCase(),

    photo:
      data?.profilePictureUrl ||
      absoluteFromApiBase(
        data?.profilePicture
      ) ||
      null,

    idPhoto:
      data?.idPhotoUrl ||
      absoluteFromApiBase(
        data?.idPhoto
      ) ||
      null,
  });

const emitCacheUpdate =
  (
    data
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(
        PROFILE_SETTINGS_CACHE_UPDATED_EVENT,
        {
          detail:
            data,
        }
      )
    );
  };

const prewarmAvatar =
  (
    data
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return Promise.resolve(
        null
      );
    }

    const url =
      data?.profile?.photo ||
      null;

    if (!url) {
      warmedAvatarUrl =
        null;

      warmedAvatarImage =
        null;

      warmedAvatarPromise =
        null;

      return Promise.resolve(
        null
      );
    }

    if (
      warmedAvatarUrl ===
        url &&
      warmedAvatarPromise
    ) {
      return warmedAvatarPromise;
    }

    const image =
      new Image();

    image.crossOrigin =
      "anonymous";

    image.decoding =
      "sync";

    warmedAvatarUrl =
      url;

    warmedAvatarImage =
      image;

    warmedAvatarPromise =
      new Promise(
        (
          resolve
        ) => {
          const finish =
            async () => {
              try {
                if (
                  typeof image.decode ===
                  "function"
                ) {
                  await image.decode();
                }
              } catch {
                // ignore
              }

              resolve(
                url
              );
            };

          image.onload =
            finish;

          image.onerror =
            () => {
              if (
                warmedAvatarUrl ===
                url
              ) {
                warmedAvatarUrl =
                  null;

                warmedAvatarImage =
                  null;

                warmedAvatarPromise =
                  null;
              }

              resolve(
                null
              );
            };

          image.src =
            url;

          if (
            image.complete &&
            image.naturalWidth >
              0
          ) {
            finish();
          }
        }
      );

    return warmedAvatarPromise;
  };

const readLegacyCache =
  () => {
    try {
      const profile =
        safeParse(
          window.localStorage.getItem(
            LEGACY_PROFILE_CACHE_KEY
          )
        ) ||
        safeParse(
          window.sessionStorage.getItem(
            LEGACY_PROFILE_CACHE_KEY
          )
        );

      const account =
        safeParse(
          window.localStorage.getItem(
            LEGACY_ACCOUNT_CACHE_KEY
          )
        ) ||
        safeParse(
          window.sessionStorage.getItem(
            LEGACY_ACCOUNT_CACHE_KEY
          )
        );

      const profileData =
        profile?.data ??
        profile ??
        null;

      const accountData =
        account?.data ??
        account ??
        null;

      if (!profileData) {
        return null;
      }

      return {
        profile:
          profileData.profile ||
          null,

        accountEmail:
          normalizeEmail(
            profileData.accountEmail ||
            profileData.profile
              ?.accountEmail ||
            accountData?.email ||
            ""
          ),

        fetchedAt:
          0,
      };
    } catch {
      return null;
    }
  };

export const readProfileSettingsCache =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return null;
    }

    const cacheKey =
      buildCacheKey();

    if (
      hotCacheKey ===
        cacheKey &&
      hotCacheEnvelope?.data
    ) {
      return {
        ...EMPTY_PROFILE_SETTINGS_DATA,
        ...hotCacheEnvelope.data,
      };
    }

    try {
      const raw =
        window.localStorage.getItem(
          cacheKey
        );

      if (!raw) {
        const legacy =
          readLegacyCache();

        if (legacy) {
          writeProfileSettingsCache(
            legacy
          );

          return legacy;
        }

        return null;
      }

      const parsed =
        safeParse(
          raw
        );

      if (
        !parsed?.data ||
        typeof parsed.savedAt !==
          "number"
      ) {
        window.localStorage.removeItem(
          cacheKey
        );

        return null;
      }

      if (
        Date.now() -
          parsed.savedAt >
        PROFILE_SETTINGS_CACHE_MAX_AGE
      ) {
        window.localStorage.removeItem(
          cacheKey
        );

        return null;
      }

      hotCacheKey =
        cacheKey;

      hotCacheEnvelope =
        parsed;

      prewarmAvatar(
        parsed.data
      ).catch(
        () => {}
      );

      return {
        ...EMPTY_PROFILE_SETTINGS_DATA,
        ...parsed.data,
      };
    } catch (
      error
    ) {
      console.warn(
        "[profileSettingsCache] read failed:",
        error
      );

      return null;
    }
  };

export const writeProfileSettingsCache =
  (
    data
  ) => {
    const nextData = {
      ...EMPTY_PROFILE_SETTINGS_DATA,
      ...(data ||
        {}),
    };

    if (
      typeof window ===
      "undefined"
    ) {
      return nextData;
    }

    const cacheKey =
      buildCacheKey();

    const envelope = {
      savedAt:
        Date.now(),

      data:
        nextData,
    };

    hotCacheKey =
      cacheKey;

    hotCacheEnvelope =
      envelope;

    prewarmAvatar(
      nextData
    ).catch(
      () => {}
    );

    try {
      window.localStorage.setItem(
        cacheKey,
        JSON.stringify(
          envelope
        )
      );
    } catch (
      error
    ) {
      console.warn(
        "[profileSettingsCache] write failed:",
        error
      );
    }

    emitCacheUpdate(
      nextData
    );

    return nextData;
  };

export const patchProfileSettingsCache =
  (
    patch
  ) => {
    localPatchRevision +=
      1;

    const current =
      readProfileSettingsCache() || {
        ...EMPTY_PROFILE_SETTINGS_DATA,
      };

    const next =
      typeof patch ===
      "function"
        ? patch(
            current
          )
        : {
            ...current,
            ...(patch ||
              {}),
          };

    return writeProfileSettingsCache(
      next
    );
  };

export const subscribeProfileSettingsCache =
  (
    callback
  ) => {
    if (
      typeof window ===
        "undefined" ||
      typeof callback !==
        "function"
    ) {
      return () => {};
    }

    const handler =
      (
        event
      ) => {
        callback(
          event?.detail ??
            readProfileSettingsCache()
        );
      };

    const storageHandler =
      (
        event
      ) => {
        if (
          event.key !==
          buildCacheKey()
        ) {
          return;
        }

        hotCacheKey =
          null;

        hotCacheEnvelope =
          null;

        callback(
          readProfileSettingsCache()
        );
      };

    window.addEventListener(
      PROFILE_SETTINGS_CACHE_UPDATED_EVENT,
      handler
    );

    window.addEventListener(
      "storage",
      storageHandler
    );

    return () => {
      window.removeEventListener(
        PROFILE_SETTINGS_CACHE_UPDATED_EVENT,
        handler
      );

      window.removeEventListener(
        "storage",
        storageHandler
      );
    };
  };

const buildRequestConfig =
  (
    force
  ) => {
    if (!force) {
      return undefined;
    }

    return {
      params: {
        _profileSettingsRefresh:
          Date.now(),
      },

      headers: {
        "Cache-Control":
          "no-cache",

        Pragma:
          "no-cache",
      },
    };
  };

export const prefetchProfileSettingsData =
  async ({
    force = false,
  } = {}) => {
    const cached =
      readProfileSettingsCache();

    if (
      !force &&
      cached?.fetchedAt &&
      Date.now() -
        cached.fetchedAt <
        PROFILE_SETTINGS_FRESH_FOR
    ) {
      return cached;
    }

    if (
      !force &&
      inFlightPromise
    ) {
      return inFlightPromise;
    }

    const requestId =
      ++requestSequence;

    latestRequestId =
      requestId;

    const patchRevisionAtStart =
      localPatchRevision;

    const requestConfig =
      buildRequestConfig(
        force
      );

    const requestPromise =
      (async () => {
        try {
          const [
            profileResult,
            accountResult,
          ] =
            await Promise.allSettled([
              api.get(
                "/users/profile",
                requestConfig
              ),

              api.get(
                "/auth/account-access",
                requestConfig
              ),
            ]);

          if (
            requestId !==
              latestRequestId ||
            patchRevisionAtStart !==
              localPatchRevision
          ) {
            return (
              readProfileSettingsCache() ||
              cached ||
              null
            );
          }

          const current =
            readProfileSettingsCache() || {
              ...EMPTY_PROFILE_SETTINGS_DATA,
            };

          const profile =
            profileResult.status ===
            "fulfilled"
              ? normalizeProfile(
                  profileResult
                    .value?.data ||
                    {}
                )
              : current.profile;

          const accountEmail =
            accountResult.status ===
            "fulfilled"
              ? normalizeEmail(
                  accountResult
                    .value?.data
                    ?.email ||
                  ""
                )
              : current.accountEmail;

          if (
            !profile &&
            accountResult.status !==
              "fulfilled"
          ) {
            return current;
          }

          const next = {
            profile,
            accountEmail,
            fetchedAt:
              Date.now(),
          };

          if (
            requestId !==
              latestRequestId ||
            patchRevisionAtStart !==
              localPatchRevision
          ) {
            return (
              readProfileSettingsCache() ||
              cached ||
              null
            );
          }

          return writeProfileSettingsCache(
            next
          );
        } catch (
          error
        ) {
          console.warn(
            "[profileSettingsCache] prefetch failed:",
            error
          );

          return (
            readProfileSettingsCache() ||
            cached ||
            null
          );
        }
      })();

    inFlightPromise =
      requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (
        inFlightPromise ===
        requestPromise
      ) {
        inFlightPromise =
          null;
      }
    }
  };

export const prewarmProfileSettingsAvatarFromCache =
  () =>
    prewarmAvatar(
      readProfileSettingsCache()
    );